import Api, { handleOPRestApiError } from "@op/opds.api-base";
import log from "@op/opds.api-base/log";
import { makeAutoObservable, runInAction } from "mobx";
import {
  resolveBrowserAndOS,
  resolveCaseType,
  resolveSubjectByTarget,
} from "../utils/commonUtils";
import { getCsrf } from "../utils/utilities";
import {
  BUSINESS,
  HA,
  INSURANCE,
  MAX_ERROR_COUNT,
  UNIDENTIFIED,
  YA,
  SOURCE,
  ASSIGNEE_GROUPS,
  PERSON,
  OP,
  CUSTOMER_FEEDBACK_CLAIMS_INDEMNITIES,
  CUSTOMER_FEEDBACK_INSURANCE_SAVING,
} from "../utils/enums";

class RestStore {
  api = null;
  apiRoot = null;
  restUrl = null;

  loading = true;
  initialLoadError = false;
  fatalError = false;

  isSending = false;
  errorCount = 0;
  showError = false;
  feedbackId = "";

  customerInfoReceived = false;
  customerType = UNIDENTIFIED;
  customership = PERSON;
  email = null;
  phone = null;
  address = null;
  postalCode = null;
  csrf = null;

  constructor() {
    makeAutoObservable(this);
  }

  initialiseApi = (apiRoot, restUrl) => {
    this.restUrl = restUrl;
    this.apiRoot = apiRoot;

    this.csrf = getCsrf();

    this.api = new Api(apiRoot, {
      timeout: 15000,
      onError: handleOPRestApiError,
      onResponse: (response) => {
        const contentType = response.headers.get("Content-Type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("Invalid response content type");
        }
        return response;
      },
    });
    this.api.defaultHeaders.set("Accept", "application/json");
    this.api.defaultHeaders.set("Content-Type", "application/json");
    this.api.defaultHeaders.set(this.csrf.headerName, this.csrf.token);
    log.config(this.api).install();
  };

  reset = () => {
    this.initialLoadError = false;
    this.errorCount = 0;
    this.isSending = false;
    this.showError = false;
    this.feedbackId = "";
  };

  loadUserInformation = (callback, redirect) => {
    return new Promise((resolve, reject) => {
      this.api
        .get(`${this.restUrl}/userinformation`)
        .then((response) => {
          runInAction(() => {
            this.loading = false;
            this.updateUserInformation(response);
          });
          callback();
          resolve();
        })
        .catch((e) => {
          log.captureException(e);

          runInAction(() => {
            this.loading = false;
            this.initialLoadError = true;
          });

          redirect();
          reject();
        });
    });
  };

  resolveCustomerType = (response) => {
    if (response.authenticationMethod === OP) {
      return response.partyCustomership;
    }

    return response.authenticationMethod;
  };

  updateUserInformation = (response) => {
    this.customerInfoReceived = true;
    this.customerType = this.resolveCustomerType(response);
    this.customership = response.partyCustomership;
    this.email = response.partyEmail;
    this.address = response.partyAddress;
    this.postalCode = response.partyPostalCode;
    if (response.partyPhone) {
      this.phone = response.partyPhone.replace("+358", "0");
    }
  };

  sendFeedback = (feedback, callback, redirect) => {
    return new Promise((resolve, reject) => {
      this.isSending = true;
      this.showError = false;
      this.api
        .post(`${this.restUrl}/feedbacks`, {
          body: JSON.stringify(this.buildDataObject(feedback)),
        })
        .then((response) => {
          this.isSending = false;

          if (response.ticketId) {
            this.feedbackId = response.ticketId;
            redirect(true);
          } else {
            this.errorCount += 1;
            this.showError = true;
            if (this.errorCount === MAX_ERROR_COUNT) {
              redirect(false);
            } else {
              callback();
            }
          }

          resolve();
        })
        .catch((e) => {
          log.captureException(e);

          this.isSending = false;
          this.errorCount += 1;
          if (this.errorCount === MAX_ERROR_COUNT) {
            redirect(false);
          } else {
            this.showError = true;
            callback();
          }
          reject();
        });
    });
  };

  buildDataObject = (feedback) => {
    const subject = resolveSubjectByTarget(
      feedback.target.value,
      feedback.subject.value
    );

    return {
      customership: this.customership === BUSINESS ? YA : HA,
      caseType: resolveCaseType(feedback.target.value),
      partyName: this.resolveValue(feedback.name),
      partyPhone: this.resolveValue(feedback.phone),
      partyEmail: this.resolveValue(feedback.email),
      partyAddress: this.resolveValue(feedback.address),
      partyPostalCode: this.resolveValue(feedback.postalCode),
      source: SOURCE,
      body: {
        feedbackSubType: feedback.type.value,
        feedback: feedback.text.value,
        topic: feedback.topic.value,
        subject,
        browser: resolveBrowserAndOS(),
        contactCustomer: feedback.contactCustomer.value,
        communicationType: this.resolveValue(feedback.contactMethod),
      },
      serviceTime: this.resolveValue(feedback.reachability),
      workQueue: {
        assigneeGroup: this.resolveAssigneeGroup(feedback),
      },
    };
  };

  resolveValue = (item) => {
    return item.included ? item.value : "";
  };

  resolveAssigneeGroup = (feedback) => {
    if (feedback.target.value === INSURANCE) {
      let assigneeGroup;
      switch (feedback.subject.value) {
        case CUSTOMER_FEEDBACK_CLAIMS_INDEMNITIES:
          assigneeGroup = ASSIGNEE_GROUPS.indemnificationFeedback;
          break;
        case CUSTOMER_FEEDBACK_INSURANCE_SAVING:
          assigneeGroup = ASSIGNEE_GROUPS.lifeInsuranceFeedback;
          break;
        default:
          assigneeGroup = ASSIGNEE_GROUPS.insuranceFeedback;
          break;
      }
      return assigneeGroup;
    } else if (this.customership === BUSINESS) {
      return ASSIGNEE_GROUPS.corporateFeedback;
    } else {
      return ASSIGNEE_GROUPS.personalFeedback;
    }
  };

  logError = (e) => {
    this.fatalError = true;
    log.captureException(e);
  };

  keepSessionAlive = () => {
    return new Promise((resolve) => {
      this.api
        .get(`${this.restUrl}/keep-alive`)
        .then(() => resolve())
        .catch((e) => log.captureException(e));
    });
  };
}

const restStore = new RestStore();

export default restStore;
